import type {
  ApiResponse,
  C2C1MappingPayload,
  CurrentStateRow,
  DashboardSummary,
  LifecycleStage,
  MovementDocumentPayload,
  MovementRow,
  Pagination,
  SearchResult,
  RegionCode,
} from '@/types'
import { apiClient } from '@/api'
import { mapRegionClients, tagRowsWithRegion } from '@/utils/regionApi'

export type SearchType = 'tag' | 'document' | 'sender' | 'receiver'

function sumSummary(parts: DashboardSummary[]): DashboardSummary {
  const byTransferType = new Map<string, number>()
  let totalTags = 0
  let scannableTags = 0
  let movementsToday = 0
  let tagsMovedToday = 0
  let tagsAtLeapWarehouse = 0
  let tagsAtCustomer = 0
  let nonScanMaterialsExcluded = 0

  for (const s of parts) {
    totalTags += s.totalTags || 0
    scannableTags += s.scannableTags || 0
    movementsToday += s.movementsToday || 0
    tagsMovedToday += s.tagsMovedToday || 0
    tagsAtLeapWarehouse += s.tagsAtLeapWarehouse || 0
    tagsAtCustomer += s.tagsAtCustomer || 0
    nonScanMaterialsExcluded += s.nonScanMaterialsExcluded || 0
    for (const row of s.byTransferType || []) {
      byTransferType.set(row.transferType, (byTransferType.get(row.transferType) || 0) + row.count)
    }
  }

  return {
    totalTags,
    scannableTags,
    movementsToday,
    tagsMovedToday,
    tagsAtLeapWarehouse,
    tagsAtCustomer,
    nonScanMaterialsExcluded,
    byTransferType: [...byTransferType.entries()].map(([transferType, count]) => ({
      transferType,
      count,
    })),
  }
}

function mergeLocationCounts(
  parts: Array<Array<{ location: string; count: number }> | undefined>
): Array<{ location: string; count: number }> {
  const map = new Map<string, number>()
  for (const list of parts) {
    for (const row of list || []) {
      map.set(row.location, (map.get(row.location) || 0) + row.count)
    }
  }
  return [...map.entries()].map(([location, count]) => ({ location, count }))
}

export const dashboardService = {
  async getSummary(params?: {
    c2_locations?: string[]
    created_at_from?: string
    created_at_to?: string
  }): Promise<DashboardSummary> {
    const parts = await mapRegionClients(async ({ client, region }) => {
      const { data } = await client.get<ApiResponse<DashboardSummary>>('/api/dashboard/summary', {
        params,
      })
      return { region, summary: data.data }
    })

    if (parts.length === 1) return parts[0]!.result.summary

    const byRegion: Partial<Record<RegionCode, DashboardSummary>> = {}
    for (const p of parts) {
      byRegion[p.result.region] = p.result.summary
    }
    return {
      ...sumSummary(parts.map((p) => p.result.summary)),
      byRegion,
    }
  },

  async getMovements(params?: Record<string, unknown>): Promise<{
    data: MovementRow[]
    pagination: Pagination
  }> {
    const parts = await mapRegionClients(async ({ client, region }) => {
      const { data } = await client.get('/api/dashboard/movements', { params })
      return {
        data: tagRowsWithRegion(data.data as MovementRow[], region),
        pagination: data.pagination as Pagination,
      }
    })

    if (parts.length === 1) return parts[0]!.result

    const rows = parts
      .flatMap((p) => p.result.data)
      .sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)))
    const total = parts.reduce((n, p) => n + (p.result.pagination?.total || 0), 0)
    const limit = Number(params?.limit) || parts[0]!.result.pagination.limit || 20
    const page = Number(params?.page) || 1
    return {
      data: rows,
      pagination: {
        page,
        limit,
        total,
        totalPages: Math.max(1, Math.ceil(total / limit)),
      },
    }
  },

  async getCurrentState(params?: Record<string, unknown>): Promise<{
    data: CurrentStateRow[]
    grouped: {
      bySender?: Array<{ location: string; count: number }>
      byReceiver?: Array<{ location: string; count: number }>
      byWarehouse: Array<{ location: string; count: number }>
      byCustomer: Array<{ location: string; count: number }>
    }
  }> {
    const parts = await mapRegionClients(async ({ client, region }) => {
      const { data } = await client.get('/api/dashboard/current-state', { params })
      return {
        data: tagRowsWithRegion(data.data as CurrentStateRow[], region),
        grouped: data.grouped as {
          bySender?: Array<{ location: string; count: number }>
          byReceiver?: Array<{ location: string; count: number }>
          byWarehouse: Array<{ location: string; count: number }>
          byCustomer: Array<{ location: string; count: number }>
        },
      }
    })

    if (parts.length === 1) return parts[0]!.result

    return {
      data: parts.flatMap((p) => p.result.data),
      grouped: {
        bySender: mergeLocationCounts(parts.map((p) => p.result.grouped.bySender)),
        byReceiver: mergeLocationCounts(parts.map((p) => p.result.grouped.byReceiver)),
        byWarehouse: mergeLocationCounts(parts.map((p) => p.result.grouped.byWarehouse)),
        byCustomer: mergeLocationCounts(parts.map((p) => p.result.grouped.byCustomer)),
      },
    }
  },

  async getTagJourney(rfidCode: string): Promise<{
    current: CurrentStateRow | null
    history: MovementRow[]
    region?: RegionCode
  }> {
    const parts = await mapRegionClients(async ({ client, region }) => {
      const { data } = await client.get<
        ApiResponse<{ current: CurrentStateRow | null; history: MovementRow[] }>
      >(`/api/dashboard/tag/${encodeURIComponent(rfidCode)}`)
      return {
        region,
        current: data.data.current
          ? { ...data.data.current, region }
          : null,
        history: tagRowsWithRegion(data.data.history || [], region),
      }
    })

    if (parts.length === 1) {
      const r = parts[0]!.result
      return { current: r.current, history: r.history, region: r.region }
    }

    const withData = parts.filter(
      (p) => p.result.current || (p.result.history && p.result.history.length > 0)
    )
    const use = withData.length ? withData : parts
    return {
      current: use.map((p) => p.result.current).find(Boolean) ?? null,
      history: use
        .flatMap((p) => p.result.history)
        .sort((a, b) => String(a.created_at).localeCompare(String(b.created_at))),
      region: use.length === 1 ? use[0]!.result.region : undefined,
    }
  },

  async getDocumentJourney(
    document: string,
    includeRef = false
  ): Promise<{
    document: string
    includeRef: boolean
    tagCount: number
    movementCount: number
    tags: string[]
    currentStates: CurrentStateRow[]
    history: MovementRow[]
    byTransferType: Array<{ transferType: string; count: number }>
    region?: RegionCode
  }> {
    const parts = await mapRegionClients(async ({ client, region }) => {
      const { data } = await client.get(
        `/api/dashboard/document/${encodeURIComponent(document)}`,
        { params: includeRef ? { include_ref: '1' } : undefined }
      )
      const payload = data.data
      return {
        ...payload,
        region,
        currentStates: tagRowsWithRegion(payload.currentStates || [], region),
        history: tagRowsWithRegion(payload.history || [], region),
      }
    })

    if (parts.length === 1) return parts[0]!.result

    const withData = parts.filter((p) => (p.result.movementCount || 0) > 0 || (p.result.tagCount || 0) > 0)
    const use = withData.length ? withData : parts
    if (use.length === 1) return use[0]!.result

    const history = use.flatMap((p) => p.result.history || [])
    const tags = [...new Set(use.flatMap((p) => p.result.tags || []))]
    const byTransferType = new Map<string, number>()
    for (const p of use) {
      for (const row of p.result.byTransferType || []) {
        byTransferType.set(row.transferType, (byTransferType.get(row.transferType) || 0) + row.count)
      }
    }
    return {
      document,
      includeRef,
      tagCount: tags.length,
      movementCount: history.length,
      tags,
      currentStates: use.flatMap((p) => p.result.currentStates || []),
      history,
      byTransferType: [...byTransferType.entries()].map(([transferType, count]) => ({
        transferType,
        count,
      })),
    }
  },

  async getMovementDocument(
    document: string,
    includeRef = false
  ): Promise<MovementDocumentPayload> {
    const parts = await mapRegionClients(async ({ client, region }) => {
      const { data } = await client.get<ApiResponse<MovementDocumentPayload>>(
        `/api/dashboard/movement-document/${encodeURIComponent(document)}`,
        { params: includeRef ? { include_ref: '1' } : undefined }
      )
      return {
        ...data.data,
        region,
        movements: tagRowsWithRegion(data.data.movements || [], region),
        currentStates: tagRowsWithRegion(data.data.currentStates || [], region),
      }
    })

    if (parts.length === 1) return parts[0]!.result

    const withData = parts.filter((p) => (p.result.header?.movementCount || 0) > 0)
    const use = withData.length ? withData : parts
    return use[0]!.result
  },

  async getSenderJourney(
    senderCode: string,
    params?: Record<string, unknown>
  ): Promise<{
    partyCode: string
    partyName: string | null
    partyRole: string
    uniqueTagCount: number
    uniqueDocumentCount: number
    history: MovementRow[]
    pagination: Pagination
    region?: RegionCode
  }> {
    const parts = await mapRegionClients(async ({ client, region }) => {
      const { data } = await client.get(
        `/api/dashboard/sender/${encodeURIComponent(senderCode)}`,
        { params }
      )
      return {
        ...data.data,
        region,
        history: tagRowsWithRegion(data.data.history || [], region),
      }
    })

    if (parts.length === 1) return parts[0]!.result

    const history = parts
      .flatMap((p) => p.result.history || [])
      .sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)))
    const total = parts.reduce((n, p) => n + (p.result.pagination?.total || 0), 0)
    const first = parts[0]!.result
    return {
      partyCode: first.partyCode,
      partyName: first.partyName,
      partyRole: first.partyRole,
      uniqueTagCount: parts.reduce((n, p) => n + (p.result.uniqueTagCount || 0), 0),
      uniqueDocumentCount: parts.reduce((n, p) => n + (p.result.uniqueDocumentCount || 0), 0),
      history,
      pagination: {
        page: first.pagination?.page || 1,
        limit: first.pagination?.limit || 20,
        total,
        totalPages: Math.max(1, Math.ceil(total / (first.pagination?.limit || 20))),
      },
    }
  },

  async getReceiverJourney(
    receiverCode: string,
    params?: Record<string, unknown>
  ): Promise<{
    partyCode: string
    partyName: string | null
    partyRole: string
    uniqueTagCount: number
    uniqueDocumentCount: number
    history: MovementRow[]
    pagination: Pagination
    region?: RegionCode
  }> {
    const parts = await mapRegionClients(async ({ client, region }) => {
      const { data } = await client.get(
        `/api/dashboard/receiver/${encodeURIComponent(receiverCode)}`,
        { params }
      )
      return {
        ...data.data,
        region,
        history: tagRowsWithRegion(data.data.history || [], region),
      }
    })

    if (parts.length === 1) return parts[0]!.result

    const history = parts
      .flatMap((p) => p.result.history || [])
      .sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)))
    const total = parts.reduce((n, p) => n + (p.result.pagination?.total || 0), 0)
    const first = parts[0]!.result
    return {
      partyCode: first.partyCode,
      partyName: first.partyName,
      partyRole: first.partyRole,
      uniqueTagCount: parts.reduce((n, p) => n + (p.result.uniqueTagCount || 0), 0),
      uniqueDocumentCount: parts.reduce((n, p) => n + (p.result.uniqueDocumentCount || 0), 0),
      history,
      pagination: {
        page: first.pagination?.page || 1,
        limit: first.pagination?.limit || 20,
        total,
        totalPages: Math.max(1, Math.ceil(total / (first.pagination?.limit || 20))),
      },
    }
  },

  async getLifecycle(rfidCode: string): Promise<{
    rfidCode: string
    current: CurrentStateRow | null
    history: MovementRow[]
    stages: LifecycleStage[]
    uniqueTransferTypes: string[]
    currentStage: Record<string, unknown> | null
    stageCount: number
    region?: RegionCode
  }> {
    const parts = await mapRegionClients(async ({ client, region }) => {
      const { data } = await client.get(
        `/api/dashboard/lifecycle/${encodeURIComponent(rfidCode)}`
      )
      return {
        ...data.data,
        region,
        current: data.data.current ? { ...data.data.current, region } : null,
        history: tagRowsWithRegion(data.data.history || [], region),
      }
    })

    if (parts.length === 1) return parts[0]!.result

    const withData = parts.filter(
      (p) => p.result.current || (p.result.history && p.result.history.length > 0)
    )
    const use = withData.length ? withData : parts
    if (use.length === 1) return use[0]!.result

    const history = use
      .flatMap((p) => p.result.history || [])
      .sort((a, b) => String(a.created_at).localeCompare(String(b.created_at)))
    return {
      rfidCode,
      current: use.map((p) => p.result.current).find(Boolean) ?? null,
      history,
      stages: use.flatMap((p) => p.result.stages || []),
      uniqueTransferTypes: [...new Set(use.flatMap((p) => p.result.uniqueTransferTypes || []))],
      currentStage: use.map((p) => p.result.currentStage).find(Boolean) ?? null,
      stageCount: use.reduce((n, p) => n + (p.result.stageCount || 0), 0),
    }
  },

  async getC2C1Mapping(bpCode: string): Promise<C2C1MappingPayload> {
    const { data } = await apiClient.get<ApiResponse<C2C1MappingPayload>>(
      `/api/dashboard/c2-c1-mapping/${encodeURIComponent(bpCode)}`,
      { timeout: 120_000 }
    )
    return data.data
  },

  async search(
    q: string,
    type: SearchType,
    limit = 20
  ): Promise<{ type: SearchType; results: SearchResult[] }> {
    const parts = await mapRegionClients(async ({ client, region }) => {
      const { data } = await client.get<
        ApiResponse<{ type: SearchType; results: SearchResult[] }>
      >('/api/dashboard/search', { params: { q, type, limit } })
      return {
        type: data.data.type,
        results: (data.data.results || []).map((r) => ({
          ...r,
          region,
          meta: { ...r.meta, region },
        })),
      }
    })

    const multi = parts.length > 1
    return {
      type: parts[0]?.result.type ?? type,
      results: parts.flatMap((p) =>
        p.result.results.map((r) => ({
          ...r,
          label: multi ? `${r.label} · ${p.region}` : r.label,
        }))
      ),
    }
  },
}
